import { readLayoutObject, writeLayoutMember } from '../shell/layoutStore';
import {
  GEOMETRY,
  minSize,
  ZONES,
  type DockLayout,
  type DockZone,
  type Rect,
  type WindowLayout,
  type ZoneLayout,
} from './layout';

/** The member of the user's layout object that holds the tool-window layout (spec 6.8.8). */
export const DOCK_MEMBER = 'dock';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isZone = (value: unknown): value is DockZone => (ZONES as readonly unknown[]).includes(value);

function readRect(value: unknown): Rect | null {
  if (!isObject(value)) return null;
  const { x, y, width, height } = value;
  if (!isNumber(x) || !isNumber(y) || !isNumber(width) || !isNumber(height)) return null;
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(Math.max(width, GEOMETRY.floating.minWidth)),
    height: Math.round(Math.max(height, GEOMETRY.floating.minHeight)),
  };
}

function readWindow(value: unknown, fallback: WindowLayout): WindowLayout {
  if (!isObject(value)) return fallback;
  return {
    visible: typeof value.visible === 'boolean' ? value.visible : fallback.visible,
    mode: value.mode === 'docked' || value.mode === 'floating' ? value.mode : fallback.mode,
    zone: isZone(value.zone) ? value.zone : fallback.zone,
    rect: value.rect === null ? null : (readRect(value.rect) ?? fallback.rect),
  };
}

function readZone(
  value: unknown,
  zone: DockZone,
  fallback: ZoneLayout,
): Omit<ZoneLayout, 'tabs'> & { tabs: unknown[] } {
  if (!isObject(value)) return fallback;
  return {
    size: isNumber(value.size) ? Math.round(Math.max(value.size, minSize(zone))) : fallback.size,
    tabs: Array.isArray(value.tabs) ? value.tabs : fallback.tabs,
    active: typeof value.active === 'string' ? value.active : value.active === null ? null : fallback.active,
    collapsed: typeof value.collapsed === 'boolean' ? value.collapsed : fallback.collapsed,
  };
}

/**
 * Reads a stored layout member by member against the defaults (spec 6.8.8): unknown windows are dropped,
 * missing or invalid entries take the window's default, a window missing from its zone's tabs is appended,
 * and sizes and rectangles below the minimums are raised to them.
 */
export function sanitizeDock(raw: unknown, defaults: DockLayout): DockLayout {
  if (!isObject(raw)) return defaults;
  const known = Object.keys(defaults.windows);
  const storedWindows = isObject(raw.windows) ? raw.windows : {};
  const windows = Object.fromEntries(
    known.map((id) => [id, readWindow(storedWindows[id], defaults.windows[id])]),
  ) as Record<string, WindowLayout>;

  const storedZones = isObject(raw.zones) ? raw.zones : {};
  const zones = {} as Record<DockZone, ZoneLayout>;
  for (const zone of ZONES) {
    const read = readZone(storedZones[zone], zone, defaults.zones[zone]);
    const tabs: string[] = [];
    for (const id of read.tabs) {
      if (typeof id === 'string' && windows[id]?.zone === zone && !tabs.includes(id)) tabs.push(id);
    }
    for (const id of known) if (windows[id].zone === zone && !tabs.includes(id)) tabs.push(id);
    const active = read.active !== null && tabs.includes(read.active) ? read.active : null;
    zones[zone] = { size: read.size, tabs, active, collapsed: read.collapsed };
  }

  const floating: string[] = [];
  if (Array.isArray(raw.floating)) {
    for (const id of raw.floating) {
      if (typeof id === 'string' && windows[id]?.mode === 'floating' && !floating.includes(id))
        floating.push(id);
    }
  }
  for (const id of known) if (windows[id].mode === 'floating' && !floating.includes(id)) floating.push(id);

  const storedViews = isObject(raw.views) ? raw.views : {};
  const views = Object.fromEntries(
    known.filter((id) => id in storedViews).map((id) => [id, storedViews[id]]),
  );
  return { windows, zones, floating, views };
}

/** The user's stored layout, sanitized; the defaults when nothing usable is stored. Never throws. */
export function readDock(user: string | null, defaults: DockLayout): DockLayout {
  if (!user) return defaults;
  return sanitizeDock(readLayoutObject(user)[DOCK_MEMBER], defaults);
}

/** Stores the layout in the user's layout object, keeping its other members (the theme). */
export function writeDock(user: string | null, layout: DockLayout): void {
  if (user) writeLayoutMember(user, DOCK_MEMBER, layout);
}
